import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { endpoints } from '../../api/api';
import { useAppDispatch } from '../../store/hooks';
import { setEmailVerified } from '../../store/slices/authslice';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'already_verified'>('loading');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await axios.get(`${endpoints.verifyEmail}?token=${token}`);

        if (response.data.alreadyVerified) {
          setStatus('already_verified');
          setMessage('Your email is already verified!');
        } else {
          setStatus('success');
          setMessage('Email verified successfully!');
        }

        // Update Redux state to hide the verification banner
        dispatch(setEmailVerified(true));

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);

      } catch (error: any) {
        console.error('Email verification failed:', error);
        setStatus('error');
        setMessage(error.response?.data?.message || 'Failed to verify email. Please try again.');
      }
    };

    verifyEmail();
  }, [token, navigate, dispatch]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent"></div>
        );
      case 'success':
      case 'already_verified':
        return (
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-gray-600';
      case 'success':
      case 'already_verified':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8 w-full max-w-md text-center">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img src="/logo.png" alt="IndiePress" className="w-10 h-10 mr-3" />
          <span className="text-gray-900 text-2xl font-semibold">IndiePress</span>
        </div>

        {/* Status Icon */}
        <div className="flex justify-center mb-6">
          {getStatusIcon()}
        </div>

        {/* Message */}
        <h2 className={`text-xl font-semibold mb-4 ${getStatusColor()}`}>
          {status === 'loading' ? 'Verifying Email' : status === 'success' || status === 'already_verified' ? 'Email Verified!' : 'Verification Failed'}
        </h2>
        <p className="text-gray-600 mb-6">{message}</p>

        {/* Action Buttons */}
        {status === 'success' || status === 'already_verified' ? (
          <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
        ) : status === 'error' ? (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-white text-gray-700 py-3 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Back to Login
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default VerifyEmail;
